import moment from 'moment-timezone';
import { RULE_TYPE_DEFINITIONS } from '../constants/ruleTypeDefinitions';
import { countsTowardViolationStats, isPlanDeviationRuleType } from './violationLimitActual';

/** Trading-rules calendar day / hour buckets (matches server EST cutoff). */
export const TRADING_RULES_TZ = 'America/New_York';

/**
 * Aggregate violations into time buckets for charting.
 * today        → by hour (ET)
 * week/7days   → by day (ET)
 * month/30days → by day (ET)
 * quarter      → by day (ET)
 * year         → by week (ISO week Monday in ET)
 * all          → by month (ET)
 */
export function aggregateViolationsOverTime(violations, timeRange, options = {}) {
  if (!violations || violations.length === 0) return [];
  const includePlanDeviations = options.includePlanDeviations === true;

  const aggregated = new Map();
  const ruleTypes = Object.keys(RULE_TYPE_DEFINITIONS);

  violations.forEach((violation) => {
    if (!violation.violation_timestamp || !violation.rule_type) return;
    if (!includePlanDeviations && !countsTowardViolationStats(violation)) return;
    if (includePlanDeviations && !countsTowardViolationStats(violation) && !isPlanDeviationRuleType(violation.rule_type)) return;

    const m = moment(violation.violation_timestamp).tz(TRADING_RULES_TZ);
    if (!m.isValid()) return;

    let bucketKey;
    if (timeRange === 'today') {
      bucketKey = m.clone().startOf('hour').toISOString();
    } else if (timeRange === 'year' || timeRange === 'ytd') {
      bucketKey = m.clone().startOf('isoWeek').toISOString();
    } else if (timeRange === 'all') {
      bucketKey = m.clone().startOf('month').toISOString();
    } else {
      bucketKey = m.clone().startOf('day').toISOString();
    }

    if (!aggregated.has(bucketKey)) {
      const bucket = { timestamp: bucketKey, total: 0 };
      ruleTypes.forEach((type) => {
        bucket[type] = 0;
      });
      aggregated.set(bucketKey, bucket);
    }

    const bucket = aggregated.get(bucketKey);
    bucket.total += 1;
    if (bucket[violation.rule_type] !== undefined) {
      bucket[violation.rule_type] += 1;
    } else {
      bucket[violation.rule_type] = 1;
    }
  });

  return Array.from(aggregated.values()).sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
}

/**
 * Fill missing buckets from range start through now so the chart axis is continuous.
 */
export function fillViolationBuckets(data, timeRange) {
  if (!data || data.length === 0) return [];

  const filled = [];
  const dataMap = new Map();
  data.forEach((item) => {
    const dateKey = item.date || item.timestamp;
    dataMap.set(dateKey, item);
  });

  const makeEmpty = (dateKey) => {
    const entry = { date: dateKey, timestamp: dateKey, total: 0 };
    Object.keys(RULE_TYPE_DEFINITIONS).forEach((rt) => {
      entry[rt] = 0;
    });
    return entry;
  };

  const pushBucket = (m) => {
    const dateKey = m.toISOString();
    filled.push(dataMap.get(dateKey) || makeEmpty(dateKey));
  };

  const now = moment().tz(TRADING_RULES_TZ);

  if (timeRange === 'today') {
    const current = now.clone().startOf('day');
    const end = now.clone().startOf('hour');
    while (current.isSameOrBefore(end)) {
      pushBucket(current.clone().startOf('hour'));
      current.add(1, 'hour');
    }
    return filled;
  }

  if (timeRange === 'year' || timeRange === 'ytd') {
    const current = (timeRange === 'ytd'
      ? now.clone().startOf('year')
      : now.clone().subtract(364, 'days')
    ).startOf('isoWeek');
    const end = now.clone().startOf('isoWeek');
    while (current.isSameOrBefore(end)) {
      pushBucket(current.clone().startOf('isoWeek'));
      current.add(1, 'week');
    }
    return filled;
  }

  if (timeRange === 'all') {
    const allKeys = Array.from(dataMap.keys()).sort();
    const first = allKeys.length > 0
      ? moment(allKeys[0]).tz(TRADING_RULES_TZ).startOf('month')
      : moment(0).tz(TRADING_RULES_TZ).startOf('month');
    const current = first.clone();
    const end = now.clone().startOf('month');
    while (current.isSameOrBefore(end)) {
      pushBucket(current.clone().startOf('month'));
      current.add(1, 'month');
    }
    return filled;
  }

  let daysBack = 6;
  if (timeRange === '30days' || timeRange === 'month') daysBack = 29;
  else if (timeRange === 'quarter') daysBack = 89;

  const current = now.clone().subtract(daysBack, 'days').startOf('day');
  const end = now.clone().startOf('day');
  while (current.isSameOrBefore(end)) {
    pushBucket(current.clone().startOf('day'));
    current.add(1, 'day');
  }
  return filled;
}

export function formatViolationBucketLabel(dateStr, timeRange, chartDataLength = 0) {
  if (!dateStr) return '';
  const m = moment(dateStr).tz(TRADING_RULES_TZ);
  if (!m.isValid()) return '';

  if (timeRange === 'today') {
    return m.format('h A');
  }
  if (timeRange === 'all') {
    return m.format('MMM YYYY');
  }
  if (timeRange === 'year' || timeRange === 'ytd') {
    return m.format('MMM D');
  }
  if (chartDataLength <= 7) {
    return m.format('ddd D');
  }
  return m.format('MMM D');
}
